import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { addTenant, updateTenant } from '../../services/tenantService';
import { uploadFile, removeFile } from 'services/fileService';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { FilePreview } from 'Components/Common/FilePreview';
import { uploadFieldCategoryMap } from 'Components/constants/uploadFieldCategoryMap';

interface TenantModelProps {
  isOpen: boolean;
  toggle: () => void;
  propertyId: string;
  subPropertyId: string;
  onSuccess: () => void;
  editData?: any;
}

type UploadedFile = {
  url: string;
  uploadedAt: number;
};

type UploadField = 'rentAgreement' | 'photo';

const schema = yup.object().shape({
  tenantName: yup.string().required('Tenant name is required'),
  contactNumber: yup.string().nullable(),
  email: yup.string().email('Invalid email format').nullable(),
  gstin: yup.string().nullable(),
  pan: yup.string().nullable(),
  address: yup.string().nullable(),
});

const TenantModel = ({
  isOpen,
  toggle,
  propertyId,
  subPropertyId,
  onSuccess,
  editData,
}: TenantModelProps) => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tenantName: '',
      contactNumber: '',
      email: '',
      gstin: '',
      pan: '',
      address: '',
    },
  });
  const dispatch = useDispatch();
  const [uploads, setUploads] = useState<Record<UploadField, UploadedFile[]>>({
    rentAgreement: [],
    photo: [],
  });

  const handleMultiUpload = async (
    files: FileList | null,
    field: UploadField,
  ) => {
    if (!files || !files.length) return;

    dispatch(isVisible(true));
    try {
      const fileArr = Array.from(files);

      const results = await Promise.all(
        fileArr.map(file =>
          uploadFile({
            file,

            category: uploadFieldCategoryMap[field],

            customFileName: `${field}-${Date.now()}`,

            propertyName: editData?.property?.name,

            subPropertyName: editData?.subProperty?.name,
          }),
        ),
      );

      const newFiles: UploadedFile[] = results.map(r => ({
        url: r.data.url,
        uploadedAt: Date.now(),
      }));

      setUploads(prev => {
        const updated = [...newFiles, ...prev[field]] // ⬅ DESC ORDER
          .sort((a, b) => b.uploadedAt - a.uploadedAt);

        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const removeUploadedFile = async (field: UploadField, index: number) => {
    const file = uploads[field][index];

    setUploads(prev => {
      const updated = prev[field].filter((_, i) => i !== index);
      setValue(field as any, updated.map(f => f.url).join(','));
      return { ...prev, [field]: updated };
    });

    if (file?.url) {
      try {
        await removeFile([file.url]);
      } catch (e) {
        console.error('Delete failed', e);
      }
    }
  };

  useEffect(() => {
    if (editData) {
      reset({
        tenantName: editData.tenantName || '',
        contactNumber: editData.contactNumber || '',
        email: editData.email || '',
        gstin: editData.gstin || '',
        pan: editData.pan || '',
        address: editData.address || '',
      });

      const rentAggrementUrls = (editData.rentAgreementDoc || '')
        .split(',')
        .filter(Boolean);
      const photoUrls = (editData.photo || editData.photos || '')
        .split(',')
        .filter(Boolean);
      setUploads({
        rentAgreement: rentAggrementUrls.map(url => ({
          url,
          uploadedAt: Date.now(),
        })),
        photo: photoUrls.map(url => ({ url, uploadedAt: Date.now() })),
      });
    } else {
      reset({
        tenantName: '',
        contactNumber: '',
        email: '',
        gstin: '',
        pan: '',
        address: '',
      });
      setUploads({
        rentAgreement: [],
        photo: [],
      });
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (data: any) => {
    dispatch(isVisible(true));
    try {
      const tenantData: any = {
        ...data,
        subPropertyId,
        propertyId,
      };
      delete tenantData.rentAgreement;
      if (uploads.rentAgreement)
        tenantData.rentAgreementDoc = uploads.rentAgreement
          .map(f => f.url)
          .join(',');
      if (uploads.photo)
        tenantData.photo = uploads.photo.map(f => f.url).join(',');

      if (editData) {
        await updateTenant(editData.id, tenantData);
        toast.success('Tenant updated successfully');
      } else {
        await addTenant(tenantData);
        toast.success('Tenant added successfully');
      }

      onSuccess();
      toggle();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {editData ? 'Edit Tenant' : 'Add Tenant'}
      </ModalHeader>
      <ModalBody className="overflow-hidden">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col lg={12}>
              <FormGroup>
                <Label for="tenantName" className="form-label">
                  Tenant Name *
                </Label>
                <Controller
                  name="tenantName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="tenantName"
                      className="form-control"
                      placeholder="Enter tenant name"
                      invalid={!!errors.tenantName}
                    />
                  )}
                />
                {errors.tenantName && (
                  <span className="text-danger">
                    {errors.tenantName.message}
                  </span>
                )}
              </FormGroup>
            </Col>

            <Col lg={6}>
              <FormGroup>
                <Label for="contactNumber" className="form-label">
                  Contact Number
                </Label>
                <Controller
                  name="contactNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      id="contactNumber"
                      className="form-control"
                      placeholder="Enter contact number"
                      invalid={!!errors.contactNumber}
                    />
                  )}
                />
                {errors.contactNumber && (
                  <span className="text-danger">
                    {errors.contactNumber.message}
                  </span>
                )}
              </FormGroup>
            </Col>

            <Col lg={6}>
              <FormGroup>
                <Label for="email" className="form-label">
                  Email
                </Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="email"
                      id="email"
                      className="form-control"
                      placeholder="Enter email address"
                      invalid={!!errors.email}
                    />
                  )}
                />
                {errors.email && (
                  <span className="text-danger">{errors.email.message}</span>
                )}
              </FormGroup>
            </Col>

            <Col lg={6}>
              <FormGroup>
                <Label for="gstin" className="form-label">
                  GSTIN
                </Label>
                <Controller
                  name="gstin"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      id="gstin"
                      className="form-control"
                      placeholder="Enter GSTIN"
                      invalid={!!errors.gstin}
                    />
                  )}
                />
                {errors.gstin && (
                  <span className="text-danger">{errors.gstin.message}</span>
                )}
              </FormGroup>
            </Col>

            <Col lg={6}>
              <FormGroup>
                <Label for="pan" className="form-label">
                  PAN
                </Label>
                <Controller
                  name="pan"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      id="pan"
                      className="form-control"
                      placeholder="Enter PAN"
                      invalid={!!errors.pan}
                    />
                  )}
                />
                {errors.pan && (
                  <span className="text-danger">{errors.pan.message}</span>
                )}
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup>
                <Label for="rentAgreementDoc" className="form-label">
                  Rent Agreement Document
                </Label>
                <Input
                  id="rentAgreementDoc"
                  type="file"
                  multiple
                  onChange={e =>
                    handleMultiUpload(e.target.files, 'rentAgreement')
                  }
                />
                <FilePreview
                  files={uploads.rentAgreement}
                  onRemove={i => removeUploadedFile('rentAgreement', i)}
                />
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup>
                <Label for="tenantPhoto" className="form-label">
                  Photo
                </Label>
                <Input
                  id="tenantPhoto"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={e => handleMultiUpload(e.target.files, 'photo')}
                />
                <FilePreview
                  files={uploads.photo}
                  onRemove={i => removeUploadedFile('photo', i)}
                />
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup>
                <Label for="address" className="form-label">
                  Address
                </Label>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      id="address"
                      className="form-control"
                      placeholder="Enter address"
                      rows={3}
                      invalid={!!errors.address}
                    />
                  )}
                />
                {errors.address && (
                  <span className="text-danger">{errors.address.message}</span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <div className="text-end mt-4">
            <Button color="secondary" className="me-2" onClick={toggle}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editData ? 'Update' : 'Add'} Tenant
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default TenantModel;
